<script setup>
import { ref, onMounted, computed } from "vue";
import axios from "@/utils/axios";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useLoading } from "vue-loading-overlay";
import loaderData from "@/utils/loader-data";
import { useRouter } from "vue-router";
import WorkflowViewModal from "@/components/tasks/WorkflowViewModal.vue";

const router = useRouter();
const $loading = useLoading(loaderData);
const route = useRoute("tasks-details-id");

const task = ref({});
const canCompleteTask = ref(false);
const canViewAllTasks = ref(false);
const isAssignedToUser = ref(false);
const workflow = ref({});
const workflowStep = ref({});
const workflowDialogVisible = ref(false);

const goToWorkflowDetails = () => {
  if (workflow.value?.id) {
    workflowDialogVisible.value = true;
  }
};

const goToWorkflowStepView = () => {
  if (workflow.value?.id) {
    const stepId = workflowStep.value?.id || task.value.workflow_step_id;
    router.push({
      path: `/workflows/view/${workflow.value.id}`,
      query: stepId ? { step_id: stepId } : undefined,
    });
  }
};

const formattedDescription = computed(() =>
  task.value.description
    ? task.value.description.replace(/\r\n|\n|\r/g, '<br>')
    : ""
);

const fetchWorkflow = () => {
    axios
        .get(`/workflows/${task.value.workflow_id}`)
        .then((response) => {
            if (response.status == "success") {
                console.log('Fetched workflow:', response);
                workflow.value = response.data.workflow;
            }
        })
        .catch((error) => {
            console.error('Error fetching workflow:', error);
        });
};

const fetchWorkflowStep = () => {
    console.log('Fetching workflow step for ID:', task.value.workflow_step_id);
    axios
        .get(`/workflow_steps/${task.value.workflow_step_id}`)
        .then((response) => {
            if (response.status == "success") {
                console.log('Fetched workflow step:', response);
                workflowStep.value = response.data.workflow_step;
            }
        })
        .catch((error) => {
            console.error('Error fetching workflow step:', error);
        });
};

// Get the task details
const getTaskDetails = () => {
  let id = route.params.id;
  let loader = $loading.show();
  axios
    .get(`/tasks/${id}`)
    .then((response) => {
      if (response.status == "success") {
        task.value = response.data.task;
        // Set permission values from API response
        canViewAllTasks.value = task.value.can_view_all_tasks;
        canCompleteTask.value = task.value.can_complete_task;
        isAssignedToUser.value = task.value.is_assigned_to_user;
        
        // Fetch related workflow and workflow step data
        if (task.value.workflow_id) {
          fetchWorkflow();
        }
        if (task.value.workflow_step_id) {
          fetchWorkflowStep();
        }
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        });
      }
    })
    .catch((error) => {
      toast.error("Failed to load task details", {
        autoClose: 2000,
      });
    })
    .finally(() => {
      loader.hide();
    });
};

// Get status color for chips
const getStatusColor = (status) => {
  switch (status) {
    case "completed":
      return "success";
    case "canceled":
      return "error";
    case "pending":
      return "warning";
    default:
      return "primary";
  }
};

// Update task status
const updateStatus = (status) => {
  let id = route.params.id;
  let loader = $loading.show();
  axios
    .post(`/tasks/${id}/status`, { status })
    .then((response) => {
      if (response.status == "success") {
        toast.success(response.message, {
          autoClose: 2000,
        });
        // Refresh task details
        getTaskDetails();
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        });
      }
    })
    .catch((error) => {
      toast.error("Failed to update task status", {
        autoClose: 2000,
      });
    })
    .finally(() => {
      loader.hide();
    });
};

// Update user status
const updateUserStatus = (userId, status) => {
  let id = route.params.id;
  let loader = $loading.show();
  axios
    .post(`/tasks/${id}/user-status`, { userId, status })
    .then((response) => {
      if (response.status == "success") {
        toast.success(response.message, {
          autoClose: 2000,
        });
        // Refresh task details
        getTaskDetails();
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        });
      }
    })
    .catch((error) => {
      toast.error("Failed to update user status", {
        autoClose: 2000,
      });
    })
    .finally(() => {
      loader.hide();
    });
};

// Return to task list
const goBack = () => {
  router.push({ name: "tasks-list" });
};

// Initialize page
onMounted(() => {
  getTaskDetails();
});

definePage({ meta: { requiresAuth: true } });
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-4">
          <span></span>
          <VBtn variant="tonal" color="light"  @click="goBack">
            Back
          </VBtn>
        </div>
    <VCard>

      <VCardText v-if="task.id">
        <VRow>
          <VCol cols="12" md="8">
            <h2 class="text-h4 mb-2">{{ task.title }}</h2>

            <div class="d-flex align-center mb-4">
              <VChip
                color="primary"
                size="small"
                variant="outlined"
                class="text-white font-weight-medium"
              >
                {{ task.type.charAt(0).toUpperCase() + task.type.slice(1) }}
              </VChip>

              <VChip
                color="primary"
                size="small"
                variant="tonal"
                class="text-white font-weight-medium ml-2"
                v-if="task.status"
              >
                {{ task.status.charAt(0).toUpperCase() + task.status.slice(1) }}
              </VChip>

              <div class=" ml-2">
                <VChip
                  color="secondary"
                  style="color:black !important;"
                  size="small"
                  variant="tonal"
              >
                Created {{ new Date(task.created_at).toLocaleString() }}
              </VChip>
              
              </div>
            </div>

            <VDivider class="mb-4" />

            <div class="mb-4">
              <h3 class="text-subtitle-1 text-primary mb-2">Description</h3>
              <p class="text-body-1" v-html="formattedDescription"></p>
            </div>
            <div class="mb-4" v-if="workflow.title">
              <h3 class="text-subtitle-1 text-primary mb-2">Workflow</h3>
              <span
                class="text-body-1 py-2"
                style="cursor: pointer; text-decoration: underline;"
                role="link"
                @click="goToWorkflowDetails"
              >
                {{ workflow.title }}
              </span>
            </div>
            <div class="mb-4" v-if="workflowStep.title">
              <h3 class="text-subtitle-1 text-primary mb-2">Workflow Step</h3>
              <span
                class="text-body-1 py-2"
                style="cursor: pointer; text-decoration: underline;"
                role="link"
                @click="goToWorkflowStepView"
              >
                {{ workflowStep.title }}
              </span>
            </div>

            <div v-if="task.workflow_task_id">
              <h3 class="text-subtitle-1 mb-2">Associated Workflow Task</h3>
              <p class="text-body-1">ID: {{ task.workflow_task_id }}</p>
            </div>

            <!-- Assigned Users Table for users with View All Tasks permission -->
            <div
              v-if="
                canViewAllTasks &&
                task.assignments &&
                task.assignments.length > 0
              "
              class="mt-6"
            >
              <h3 class="text-subtitle-1 mb-2 text-primary">Assigned Users</h3>
              <VTable>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="assignment in task.assignments"
                    :key="assignment.id"
                  >
                    <td>{{ assignment.user_name }}</td>
                    <td>
                      <VChip color="primary" size="small" variant="tonal">
                        {{
                          assignment.status.charAt(0).toUpperCase() +
                          assignment.status.slice(1)
                        }}
                      </VChip>
                    </td>
                    <td>
                      <span v-if="assignment.canceled_at">
                        {{ new Date(assignment.canceled_at).toLocaleString() }}
                      </span>
                      <span v-else-if="assignment.completed_at">
                        {{ new Date(assignment.completed_at).toLocaleString() }}
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td>
                      <div class="d-flex gap-2">
                        <VBtn
                          v-if="assignment.status !== 'completed'"
                          color="primary"
                          variant="outlined"
                          size="small"
                          @click="
                            updateUserStatus(assignment.user_id, 'completed')
                          "
                        >
                          <VIcon icon="tabler-check" size="16" class="mr-1" />
                          Complete
                        </VBtn>
                        <VBtn
                          v-if="assignment.status !== 'canceled'"
                          color="primary"
                          variant="outlined"
                          size="small"
                          @click="
                            updateUserStatus(assignment.user_id, 'canceled')
                          "
                        >
                          <VIcon icon="tabler-x" size="16" class="mr-1" />
                          Cancel
                        </VBtn>
                        <VBtn
                          v-if="assignment.status !== 'pending'"
                          color="primary"
                          variant="outlined"
                          size="small"
                          @click="
                            updateUserStatus(assignment.user_id, 'pending')
                          "
                        >
                          <VIcon icon="tabler-reload" size="16" class="mr-1" />
                          Reopen
                        </VBtn>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </VTable>
            </div>
          </VCol>

          <VCol cols="12" md="4">
            <VCard variant="outlined" class="mb-4">
              <VCardTitle>Task Status</VCardTitle>
              <VCardText>
                <div
                  v-if="canCompleteTask && isAssignedToUser"
                  class="d-flex flex-column"
                >
                  <VBtn
                    v-if="task.status !== 'completed'"
                    color="primary"
                    variant="outlined"
                    class="mb-2"
                    @click="updateStatus('completed')"
                  >
                    <VIcon icon="tabler-check" class="mr-2" />
                    Mark as Completed
                  </VBtn>

                  <VBtn
                    v-if="task.status !== 'canceled'"
                    color="primary"
                    variant="outlined"
                    class="mb-2"
                    @click="updateStatus('canceled')"
                  >
                    <VIcon icon="tabler-x" class="mr-2" />
                    Cancel Task
                  </VBtn>

                  <VBtn
                    v-if="task.status !== 'pending'"
                    color="primary"
                    variant="outlined"
                    @click="updateStatus('pending')"
                  >
                    <VIcon icon="tabler-reload" class="mr-2" />
                    Reopen Task
                  </VBtn>
                </div>

                <div
                  v-else-if="!isAssignedToUser"
                  class="text-caption text-center mt-2"
                >
                  You are not assigned to this task
                </div>

                <div v-else class="text-caption text-center mt-2">
                  You don't have permission to change task status
                </div>
              </VCardText>
            </VCard>

            <VCard v-if="task.completed_at" variant="outlined" class="mb-4">
              <VCardTitle>Completion Details</VCardTitle>
              <VCardText>
                <p>
                  <strong>Completed at:</strong>
                  {{ new Date(task.completed_at).toLocaleString() }}
                </p>
              </VCardText>
            </VCard>

            <VCard v-if="task.canceled_at" variant="outlined" class="mb-4">
              <VCardTitle>Cancellation Details</VCardTitle>
              <VCardText>
                <p>
                  <strong>Cancelled at:</strong>
                  {{ new Date(task.canceled_at).toLocaleString() }}
                </p>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </VCardText>

      <VCardText v-else>
        <VProgressCircular indeterminate color="primary" />
        <span class="ml-2">Loading task details...</span>
      </VCardText>
    </VCard>

    <WorkflowViewModal
      :is-dialog-visible="workflowDialogVisible"
      :workflow-id="workflow.id"
      :step-id="workflowStep.id || task.workflow_step_id"
      @update:is-dialog-visible="workflowDialogVisible = $event"
    />
  </div>
</template>

<style scoped>
.status-indicator {
  border-radius: 4px;
}
</style>

